import {
	Button,
	Heading,
	Icon,
	ProgressBar,
	TextStyle,
	Toast,
	Tooltip,
} from "@shopify/polaris"
import { RiskMinor } from "@shopify/polaris-icons"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFirestoreDataUpdate } from "../../hooks/useFirestoreDataUpdate"
import { useFirestoreImportHistoryUpdate } from "../../hooks/useFirestoreImportHistoryUpdate.js"
import { useGQL } from "../../hooks/useGQL"
import { usePrepareAndImportProduct } from "../../hooks/usePrepareAndImportProduct.js"
import { pushCrispEvent } from "../../utils/crisp.js"
import { GlowingButtonContainer } from "../GlowingButtonContainer.jsx"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider"
import FailedUploadsList from "./FailedUploadsList"
import SuccessfulUploadsList from "./SuccessfulUploadsList"
import handleize from "../../utils/handleize.js"

// How often buffered upload results are flushed into React state.
const UPLOAD_PROGRESS_FLUSH_MS = 400

export default function UploadMultipleProducts(props) {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { aiState, setAIState } = useContext(AIStateContext)

	const { hasImportedAProduct } = state
	const { t } = useTranslation()

	const [uploading, setUploading] = useState(false)
	const [finishedUploading, setFinishedUploading] = useState(false)

	const [productsToUploadNumber, setProductsToUploadNumber] = useState(0)
	const [successfulUploads, setSuccessfulUploads] = useState([])
	const [failedUploads, setFailedUploads] = useState([])
	const [duplicatesFound, setDuplicatesFound] = useState(false)

	const [showToast, setShowToast] = useState(true)
	const [reviewModalIsOpen, setReviewModalIsOpen] = useState(false)
	const [hasAIOrders, setHasAIOrders] = useState(false)

	const [cancelled, setCancelled] = useState(false)

	const gqlQuery = useGQL()
	const updateFirestoreUserData = useFirestoreDataUpdate()
	const updateImportHistory = useFirestoreImportHistoryUpdate()
	const prepareAndImportProduct = usePrepareAndImportProduct()

	// Results accumulate here and are flushed to state on a timer. Updating state
	// per product meant ~1000 discrete renders for a large import (React 17 does
	// not batch outside event handlers), each re-rendering the whole growing
	// results list.
	const successfulRef = useRef([])
	const failedRef = useRef([])
	const flushTimerRef = useRef(null)
	const cancelRef = useRef(false)

	const flushUploadResults = useCallback(() => {
		if (flushTimerRef.current) {
			clearTimeout(flushTimerRef.current)
			flushTimerRef.current = null
		}
		setSuccessfulUploads([...successfulRef.current])
		setFailedUploads([...failedRef.current])
	}, [])

	const scheduleFlush = useCallback(() => {
		if (flushTimerRef.current) return
		flushTimerRef.current = setTimeout(
			flushUploadResults,
			UPLOAD_PROGRESS_FLUSH_MS,
		)
	}, [flushUploadResults])

	const pushSuccessfulUpload = useCallback(
		(entry) => {
			successfulRef.current.push(entry)
			scheduleFlush()
		},
		[scheduleFlush],
	)

	const pushFailedUpload = useCallback(
		(entry) => {
			failedRef.current.push(entry)
			scheduleFlush()
		},
		[scheduleFlush],
	)

	useEffect(
		() => () => {
			if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
		},
		[],
	)

	const cancelUpload = () => {
		cancelRef.current = true
		setCancelled(true)
	}

	const uploadProducts = async (products, { preserveSuccessful } = {}) => {
		let uploadedAtLeastOne = false
		let failedUploadingAtLeastOne = false

		const checkAndUpload = async (product, id) => {
			try {
				if (state.skipDuplicates && product.handle) {
					const handleRes = await gqlQuery(
						`
						query ProductByHandle($handle: String!) {
							productByHandle(handle: $handle) {
								id
							}
						}
					`,
						{
							handle: product.handle,
						},
					).catch((error) => {
						console.error("ProductByHandle catched error", {
							error,
						})
					})

					if (handleRes?.data?.productByHandle?.id) {
						setDuplicatesFound(true)
						pushFailedUpload({
							...product,
							id: id,
							error: t("multiple.duplicate_product"),
						})
						return
					}
				} else if (!state.skipDuplicates && product.handle) {
					delete product.handle
				}

				const { _productUrl, ...productCreateInput } = product

				const aiOrders = props.bulkAIOrders

				const productCreateResult = await prepareAndImportProduct({
					product: productCreateInput,
					aiOrders,
				})

				if (
					productCreateResult.data?.productCreate?.userErrors &&
					productCreateResult.data.productCreate.userErrors.length > 0
				) {
					console.error(
						"UploadMultipleProducts productCreate userErrors",
						productCreateResult.data.productCreate.userErrors,
					)
					failedUploadingAtLeastOne = true
					pushFailedUpload({
						...product,
						id: id,
						error:
							productCreateResult.data.productCreate
								.userErrors?.[0]?.message || "",
					})
				} else if (
					productCreateResult.errors &&
					productCreateResult.errors.length > 0
				) {
					console.error(
						"UploadMultipleProducts productCreate result errors",
						productCreateResult.errors,
					)
					failedUploadingAtLeastOne = true
					pushFailedUpload({
						...product,
						id: id,
						error:
							productCreateResult?.errors?.[0]?.graphQLErrors?.[0]
								?.message ||
							productCreateResult?.errors?.[0]?.message ||
							"Unspecified error 3",
					})
				} else {
					if (
						product !== undefined &&
						productCreateResult?.data?.productCreate?.product?.id
					) {
						uploadedAtLeastOne = true
						pushSuccessfulUpload({
							...product,
							id: id,
							url:
								productCreateResult.data.productCreate.product
									.onlineStoreUrl ||
								productCreateResult.data.productCreate.product
									.onlineStorePreviewUrl,
						})
						updateImportHistory({
							productTitle: product.title || "",
							productUrl: _productUrl || props.productsUrl || "",
							productImageUrl: product.images[0]?.src || "",
							dateImported: new Date(),
							importMode: "multiple",
							productId:
								productCreateResult.data.productCreate.product
									.id || "",
						})
						pushCrispEvent("multiple_products_import_successful", {
							productsUrl: props.productsUrl,
						})
					}
				}
			} catch (err) {
				console.error(
					"UploadMultipleProducts productCreate catched error",
					err,
				)
				failedUploadingAtLeastOne = true
				pushFailedUpload({ ...product, id: id, error: err.message })
			}
		}

		cancelRef.current = false
		setCancelled(false)
		setUploading(true)
		setShowToast(true)
		setFinishedUploading(false)
		// A retry re-runs only the failed products, so wiping the successful list
		// would discard the record of everything that already worked.
		failedRef.current = []
		if (!preserveSuccessful) successfulRef.current = []
		flushUploadResults()
		setProductsToUploadNumber(
			products.length +
				(preserveSuccessful ? successfulRef.current.length : 0),
		)
		setDuplicatesFound(false)

		try {
			let uploadPromisesArray = []

			for (let i = 0; i < products.length; i++) {
				if (cancelRef.current) break

				const p = products[i]
				const { id, ...product } = p

				const promise = checkAndUpload(product, id)

				uploadPromisesArray.push(promise)

				if ((i + 1) % 5 === 0) {
					await Promise.allSettled(uploadPromisesArray)
					uploadPromisesArray = []
				}
			}

			// Last chunk uploads
			if (uploadPromisesArray.length > 0) {
				await Promise.allSettled(uploadPromisesArray)
			}

			if (uploadedAtLeastOne && !hasImportedAProduct) {
				updateFirestoreUserData({ hasImportedAProduct: true })
			}
			flushUploadResults()
			setUploading(false)
			setFinishedUploading(true)
			props.onFinishedUploading()
			if (failedUploadingAtLeastOne) {
				props.onFailedUploading()
			}
		} catch (err) {
			console.error("UploadMultipleProducts catched error", err)
			flushUploadResults()
			setUploading(false)
		}
	}

	const retryUpload = async (products) => {
		props.onStartedRetry()
		await uploadProducts(
			products.map((p) => {
				const { error, id, ...product } = p
				return product
			}),
			{ preserveSuccessful: true },
		)

		props.onFinishedUploading()
	}

	useEffect(() => {
		if (
			!uploading &&
			props.readyToUpload &&
			props.productsToUpload &&
			props.productsToUpload.length > 0
		) {
			if (props.bulkAIOrders?.length > 0) {
				setHasAIOrders(true)
			} else {
				setHasAIOrders(false)
			}
			uploadProducts(props.productsToUpload)
		}
	}, [props.readyToUpload, props.productsToUpload])

	return (
		<div>
			{hasAIOrders && (uploading || finishedUploading) ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						marginTop: "1.5rem",
						marginBottom: "1.5rem",
					}}
				>
					<div
						style={{
							maxWidth: "40rem",
						}}
					>
						<GlowingButtonContainer intensity="medium">
							<div
								style={{
									background: "#222",
									color: "#eee",
									padding: "1rem",
									borderRadius: "1rem",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
									}}
								>
									<svg
										style={{
											width: "1.3rem",
											height: "1.3rem",
										}}
										viewBox="0 0 24 24"
									>
										<path
											fill="#fff"
											d="m10 19l-2.5-5.5L2 11l5.5-2.5L10 3l2.5 5.5L18 11l-5.5 2.5L10 19Zm8 2l-1.25-2.75L14 17l2.75-1.25L18 13l1.25 2.75L22 17l-2.75 1.25L18 21Z"
										></path>
									</svg>
									<h1
										style={{
											fontSize: "1.1rem",
											lineHeight: "1",
										}}
									>
										{t(
											"ai.products_processed_by_ai_in_the_background",
										)}
									</h1>
								</div>

								{uploading ? (
									<p
										style={{
											fontSize: "0.75rem",
											lineHeight: "1.1",
											marginTop: "0.5rem",
											paddingLeft: "1.8rem",
										}}
									>
										{t("ai.products_will_be_processed_1")}
									</p>
								) : finishedUploading ? (
									<p
										style={{
											fontSize: "0.75rem",
											lineHeight: "1.1",
											marginTop: "0.5rem",
											paddingLeft: "1.8rem",
										}}
									>
										{t("ai.products_will_be_processed_2")}
									</p>
								) : null}
							</div>
						</GlowingButtonContainer>
					</div>
				</div>
			) : null}

			<div
				style={{
					marginTop: "1rem",
					display: "flex",
					justifyContent: "center",
				}}
			>
				{uploading ? (
					<div style={{ width: "40rem" }}>
						<ProgressBar
							size="small"
							progress={
								((successfulUploads.length +
									failedUploads.length) /
									productsToUploadNumber) *
								100
							}
						/>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								marginTop: "0.25rem",
							}}
						>
							{/* A large import runs for many minutes; without this the
							    only way to stop it was to close the tab. */}
							<Button
								size="slim"
								onClick={cancelUpload}
								disabled={cancelled}
								loading={cancelled}
							>
								{t("common.cancel")}
							</Button>
							<p>
								{successfulUploads.length +
									failedUploads.length}
								/{productsToUploadNumber}
							</p>
						</div>
					</div>
				) : (
					""
				)}
			</div>

			<div className="multiple-uploads-lists">
				{successfulUploads.length > 0 ? (
					<div
						style={{
							width: failedUploads.length > 0 ? "30rem" : "40rem",
						}}
					>
						<div
							style={{
								marginBottom: "1rem",
								display: "flex",
								justifyContent: "space-between",
							}}
						>
							<Heading>
								{t("multiple.successful_uploads")}
							</Heading>
							<TextStyle variation="positive">
								{successfulUploads.length}
							</TextStyle>
						</div>
						<SuccessfulUploadsList
							successfulUploads={successfulUploads}
							uploadingSubmittedProducts={uploading}
						/>
					</div>
				) : (
					""
				)}
				{failedUploads.length > 0 ? (
					<div
						style={{
							width:
								successfulUploads.length > 0
									? "30rem"
									: "40rem",
						}}
					>
						<div
							style={{
								marginBottom: "1rem",
								display: "flex",
								justifyContent: "space-between",
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
								}}
							>
								<Heading>
									{t("multiple.failed_uploads")}
								</Heading>
								<div style={{ marginLeft: "1rem" }}>
									<Tooltip
										content={
											<div
												style={{
													padding: "0.4rem 0.7rem",
												}}
											>
												{t(
													"multiple.consider_using_the_export_to_csv_option_if_uploads_keep_failing",
												)}
											</div>
										}
									>
										<Icon
											source={RiskMinor}
											color="warning"
										></Icon>
									</Tooltip>
								</div>
							</div>
							<TextStyle variation="negative">
								{failedUploads.length}
							</TextStyle>
						</div>
						<FailedUploadsList
							failedUploads={failedUploads}
							uploadingSubmittedProducts={uploading}
							retryUpload={retryUpload}
						/>
					</div>
				) : (
					""
				)}
			</div>

			{finishedUploading && showToast && (
				<Toast
					content={
						<div>
							{t("multiple.imported")} {successfulUploads.length}/
							{productsToUploadNumber} {t("multiple.products")}{" "}
							{duplicatesFound
								? t(
										"multiple.some_duplicates_were_found_and_therefore_not_imported",
									)
								: null}
						</div>
					}
					onDismiss={() => setShowToast(false)}
					duration={86400000}
				/>
			)}

			{/* {
				finishedUploading && !hasImportedAProduct && reviewModalIsOpen ?
					<Modal open={reviewModalIsOpen} onClose={() => setReviewModalIsOpen(false)} title="" primaryAction={{
						content: "Leave a review",
						onAction: () => {
							if (process.browser) {
								Object.assign(document.createElement("a"), { target: "_blank", href: "https://apps.shopify.com/kopy#reviews" }).click()
							}
						}
					}}>
						<Modal.Section>
							<Heading>Congrats on importing your first product!</Heading>
							<p>Like the app? Consider leaving us a review.</p>
						</Modal.Section>
					</Modal>
					: ""
			} */}
		</div>
	)
}
